import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import AnimatedHeroTitle from '../components/AnimatedHeroTitle';
import AnimatedCircuitry from '../components/AnimatedCircuitry';
import useAnimationPerformance from '../animations/hooks/useAnimationPerformance';
import './Pages.css';

function Home() {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const carouselRef = useRef(null);
  const heroRef = useRef(null);
  const { isMobile, isLowEndDevice } = useAnimationPerformance();

  // Parallax effect for hero background
  useEffect(() => {
    if (isMobile || isLowEndDevice) {
      if (heroRef.current) {
        heroRef.current.style.setProperty('--parallax-offset', '0px');
      }
      return undefined;
    }

    let rafId = null;
    const handleScroll = () => {
      if (!heroRef.current || rafId) {
        return;
      }

      rafId = window.requestAnimationFrame(() => {
        const scrolled = window.scrollY;
        const parallaxSpeed = 0.5;
        heroRef.current.style.setProperty('--parallax-offset', `${scrolled * parallaxSpeed}px`);
        rafId = null;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [isMobile, isLowEndDevice]);

  // Constants for carousel configuration
  const SCROLL_AMOUNT = 300;
  const SCROLL_TIMEOUT = 300;

  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) {
      return;
    }

    const track = carousel.querySelector('.clients-track');
    if (!track) {
      return;
    }

    // Clone all logos and append for seamless infinite scroll
    const logos = Array.from(track.children);
    logos.forEach((logo) => {
      const clone = logo.cloneNode(true);
      track.appendChild(clone);
    });

    // Pause animation initially to allow proper setup
    track.style.animationPlayState = 'paused';

    // Set initial scroll position AFTER DOM is ready, allowing animation to start
    // Use requestAnimationFrame to ensure layout is complete before scrolling
    requestAnimationFrame(() => {
      carousel.scrollLeft = 0;
      // Resume animation after initial setup with a small delay for stability
      setTimeout(() => {
        track.style.animationPlayState = 'running';
      }, 50);
    });

    // Add touch support for direct dragging interaction
    let touchStartX = 0;
    let lastTouchX = 0;
    let isDragging = false;
    let touchedCard = null;

    const handleTouchStart = (e) => {
      isDragging = true;
      touchStartX = e.touches[0].clientX;
      lastTouchX = touchStartX;

      // Add touch-active to the card being touched
      const card = e.target.closest('.client-card');
      if (card) {
        touchedCard = card;
        card.classList.add('touch-active');
      }

      // Pause animation on touch
      if (track) {
        track.style.animationPlayState = 'paused';
      }
    };

    const handleTouchMove = (e) => {
      if (!isDragging) {
        return;
      }

      const currentX = e.touches[0].clientX;
      const diff = lastTouchX - currentX;

      // Scroll carousel based on touch movement
      carousel.scrollLeft += diff;

      // Handle infinite scroll wrapping
      const trackWidth = track.scrollWidth / 2;
      if (carousel.scrollLeft <= 0) {
        carousel.scrollLeft = trackWidth;
      } else if (carousel.scrollLeft >= trackWidth) {
        carousel.scrollLeft = 1;
      }

      lastTouchX = currentX;
    };

    const handleTouchEnd = () => {
      isDragging = false;

      // Remove touch-active from the touched card
      if (touchedCard) {
        touchedCard.classList.remove('touch-active');
        touchedCard = null;
      }

      // Resume animation after touch ends
      if (track) {
        track.style.animationPlayState = 'running';
      }
    };

    carousel.addEventListener('touchstart', handleTouchStart, { passive: true });
    carousel.addEventListener('touchmove', handleTouchMove, { passive: true });
    carousel.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      carousel.removeEventListener('touchstart', handleTouchStart);
      carousel.removeEventListener('touchmove', handleTouchMove);
      carousel.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            // Unobserve after visibility to prevent repeated callbacks
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -100px 0px' }
    );

    const sections = document.querySelectorAll('.animate-on-scroll');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const scrollCarousel = (direction) => {
    const carousel = carouselRef.current;
    if (!carousel) {
      return;
    }

    const track = carousel.querySelector('.clients-track');
    if (!track) {
      return;
    }

    const trackWidth = track.scrollWidth / 2; // Half because we cloned

    // Pause animation during scroll
    track.style.animationPlayState = 'paused';

    if (direction === 'left') {
      carousel.scrollBy({ left: -SCROLL_AMOUNT, behavior: 'smooth' });

      // If we've scrolled to the start, jump to the end of the original content
      setTimeout(() => {
        if (carousel.scrollLeft <= 0) {
          carousel.scrollLeft = trackWidth;
        }
        // Resume animation after scroll completes
        track.style.animationPlayState = 'running';
      }, SCROLL_TIMEOUT);
    } else {
      carousel.scrollBy({ left: SCROLL_AMOUNT, behavior: 'smooth' });

      // If we've scrolled past the original content, jump back to the start
      setTimeout(() => {
        if (carousel.scrollLeft >= trackWidth) {
          carousel.scrollLeft = 0;
        }
        // Resume animation after scroll completes
        track.style.animationPlayState = 'running';
      }, SCROLL_TIMEOUT);
    }
  };

  const faqs = [
    {
      question: 'What is your typical project timeline?',
      answer:
        "Project timelines vary based on scope and complexity. Most projects range from 2-12 weeks. We'll provide a detailed timeline during our initial consultation.",
    },
    {
      question: 'Do you work with clients remotely?',
      answer:
        'Yes, we work with clients across North America. All communication and collaboration happens through modern digital tools.',
    },
    {
      question: 'What technologies do you specialize in?',
      answer:
        "We specialize in modern web technologies including React, Python, Node.js, and cloud platforms. We select the best technology stack for each project's specific needs.",
    },
    {
      question: 'How do you handle project pricing?',
      answer:
        "We offer both fixed-price and hourly arrangements depending on project scope. After understanding your requirements, we'll provide a transparent proposal with clear deliverables.",
    },
  ];

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const handleFaqKeyDown = (e, index) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleFaq(index);
    }
  };
  return (
    <main className="home-container">
      {/* Hero Section */}
      <section ref={heroRef} className="hero-section" aria-label="Introduction">
        <AnimatedCircuitry />
        <div className="hero-content">
          <AnimatedHeroTitle />
          <p className="hero-subtitle">Web Development | AI & Automation | Digital Consulting</p>
          <p className="hero-description">
            Let us automate the busy work, you focus on growth. We build systems that work while you
            sleep.
          </p>
          <div className="hero-buttons">
            <Link to="/services" className="btn btn-primary">
              Explore Our Services
            </Link>
            <Link to="/contact" className="btn btn-secondary">
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* Services Overview Section */}
      <section
        className="home-services-section animate-on-scroll"
        aria-labelledby="services-overview-heading"
      >
        <div className="container">
          <div className="card">
            <h2 id="services-overview-heading">What We Offer</h2>
            <p className="section-subtitle">
              We provide modern web solutions, automation pipelines, and AI systems designed to help
              businesses operate and scale.
            </p>
            <div className="services-grid">
              <div className="service-item">
                <span className="service-icon">🌐</span>
                <h3>Web Services</h3>
                <p>
                  SEO optimization, custom websites, web applications, and digital strategy to grow
                  your online presence.
                </p>
              </div>
              <div className="service-item">
                <span className="service-icon">🤖</span>
                <h3>AI & Automation</h3>
                <p>
                  Intelligent workflow automation, AI-powered tools, and process optimization to
                  streamline operations.
                </p>
              </div>
              <div className="service-item">
                <span className="service-icon">⚙️</span>
                <h3>Software Dev</h3>
                <p>
                  Full-stack development, APIs, data pipelines, and custom software tailored to your
                  needs.
                </p>
              </div>
              <div className="service-item">
                <span className="service-icon">💼</span>
                <h3>Consulting</h3>
                <p>
                  Technical strategy, architecture design, and expert guidance for critical
                  technology decisions.
                </p>
              </div>
            </div>
            <div style={{ textAlign: 'center', marginTop: '24px' }}>
              <Link to="/services" className="btn btn-primary">
                Explore All Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section className="clients-section animate-on-scroll" aria-labelledby="clients-heading">
        <div className="container">
          <h2 id="clients-heading" className="clients-title">
            Trusted by Leading Organizations
          </h2>
          <div className="clients-carousel-wrapper">
            <button
              className="carousel-arrow carousel-arrow-left"
              onClick={() => scrollCarousel('left')}
              aria-label="Scroll left"
            >
              ‹
            </button>
            <div
              className="clients-carousel"
              ref={carouselRef}
              role="region"
              aria-label="Client logos carousel"
            >
              <div className="clients-track">
                <div className="client-card" aria-label="RBC">
                  <div className="client-logo-img">
                    <img src="/img/RBC.svg" alt="RBC" loading="lazy" />
                  </div>
                  <span className="client-name">RBC</span>
                </div>
                <div className="client-card" aria-label="MV Concrete">
                  <div className="client-logo-img">
                    <img src="/img/MV_concrete.png" alt="MV Concrete" loading="lazy" />
                  </div>
                  <span className="client-name">MV Concrete</span>
                </div>
                <div className="client-card" aria-label="Crafted By Collins">
                  <div className="client-logo-img">
                    <img src="/img/CbyC.svg" alt="Crafted By Collins" loading="lazy" />
                  </div>
                  <span className="client-name">Crafted By Collins</span>
                </div>
                <div className="client-card" aria-label="One Ring AI">
                  <div className="client-logo-img">
                    <img src="/img/one_ring.png" alt="One Ring AI" loading="lazy" />
                  </div>
                  <span className="client-name">One Ring AI</span>
                </div>
                <div className="client-card" aria-label="Soundry">
                  <div className="client-logo-img">
                    <img src="/img/soundry.svg" alt="Soundry" loading="lazy" />
                  </div>
                  <span className="client-name">Soundry</span>
                </div>
              </div>
            </div>
            <button
              className="carousel-arrow carousel-arrow-right"
              onClick={() => scrollCarousel('right')}
              aria-label="Scroll right"
            >
              ›
            </button>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="home-values-section animate-on-scroll" aria-labelledby="values-heading">
        <div className="container">
          <div className="card">
            <h2 id="values-heading">Our Approach</h2>
            <div className="values-grid" style={{ paddingTop: '30px' }}>
              <div className="value-item">
                <span className="value-icon">🎯</span>
                <h3>Results-Driven</h3>
                <p>
                  We focus on delivering measurable outcomes that directly impact your business
                  goals.
                </p>
              </div>
              <div className="value-item">
                <span className="value-icon">🤝</span>
                <h3>Transparent</h3>
                <p>
                  Clear communication, honest timelines, and no surprises. You'll always know where
                  your project stands.
                </p>
              </div>
              <div className="value-item">
                <span className="value-icon">⚡</span>
                <h3>Pragmatic</h3>
                <p>
                  We choose the right technology for the job—not the trendy one. Practical solutions
                  that actually work.
                </p>
              </div>
            </div>
            <div style={{ textAlign: 'center', marginTop: '24px' }}>
              <Link to="/about" className="btn btn-primary">
                Learn More About Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="home-faq-section animate-on-scroll" aria-labelledby="faq-heading">
        <div className="container">
          <div className="card">
            <h2 id="faq-heading">Frequently Asked Questions</h2>
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <button
                  className="faq-question"
                  onClick={() => toggleFaq(index)}
                  onKeyDown={(e) => handleFaqKeyDown(e, index)}
                  aria-expanded={openFaqIndex === index}
                  aria-controls={`faq-answer-${index}`}
                >
                  <h3>{faq.question}</h3>
                  <span className="faq-icon" aria-hidden="true">
                    {openFaqIndex === index ? '−' : '+'}
                  </span>
                </button>
                {openFaqIndex === index && (
                  <p id={`faq-answer-${index}`} className="faq-answer">
                    {faq.answer}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="home-cta-section animate-on-scroll" aria-labelledby="cta-heading">
        <div className="container">
          <div className="card cta-card">
            <h2 id="cta-heading">Ready to Get Started?</h2>
            <p>Let's discuss how we can help transform your business.</p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary">
                Contact Us
              </Link>
              <Link to="/projects" className="btn btn-secondary">
                View Our Work
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Home;
